import React from 'react';
import {
  UNIVERSAL_INTRO,
  UNIVERSAL_SCREEN,
  LUMBAR_CE_RESCREEN,
  KNEE_OTTAWA,
  KNEE_LOCKED,
  SHOULDER_ELEVATION,
} from '../config/universalRedFlags';
import { getSupplementary } from '../config/supplementaryRedFlags';

/**
 * UniversalRedFlagsSection
 *
 * Renders the universal safety screen (7 clustered questions + follow-ups) that
 * every region template shows, plus the region major-signals for LUMBAR_SPINE
 * (cauda-equina re-screen), KNEE (Ottawa rule + locked knee) and SHOULDER (loss
 * of active elevation). Content is frozen clinical content per the signed B2
 * decision record — see frontend/src/config/universalRedFlags.js for provenance.
 *
 * Presentation only; scoring lives in utils/redFlagChecker.js (two passes).
 */
const UniversalRedFlagsSection = ({ injuryType, formData, onChange }) => {
  const set = (id) => (e) => onChange(id, e.target.value);
  const supplementary = getSupplementary(injuryType);

  const renderOptions = (item) => (
    <div className="radio-group">
      {(item.options || ['Yes', 'No']).map((opt) => (
        <label className="radio-option" key={opt}>
          <input type="radio" name={item.id} value={opt} checked={formData[item.id] === opt} onChange={set(item.id)} />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );

  const toggleMulti = (id, option) => {
    const current = Array.isArray(formData[id]) ? formData[id] : [];
    const next = current.includes(option)
      ? current.filter((o) => o !== option)
      : [...current, option];
    onChange(id, next);
  };

  const renderFollowup = (fu) => {
    const value = formData[fu.id];
    if (fu.type === 'checkbox-multiple') {
      const current = Array.isArray(value) ? value : [];
      return (
        <div className="field-group" key={fu.id}>
          <label className="field-label">{fu.label}</label>
          <div className="checkbox-group">
            {fu.options.map((opt) => (
              <label className="radio-option" key={opt}>
                <input type="checkbox" checked={current.includes(opt)} onChange={() => toggleMulti(fu.id, opt)} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }
    if (fu.type === 'radio') {
      return (
        <div className="field-group" key={fu.id}>
          <label className="field-label">{fu.label}</label>
          <div className="radio-group">
            {fu.options.map((opt) => (
              <label className="radio-option" key={opt}>
                <input type="radio" name={fu.id} value={opt} checked={value === opt} onChange={set(fu.id)} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }
    if (fu.type === 'textarea') {
      return (
        <div className="field-group" key={fu.id}>
          <label className="field-label">{fu.label}</label>
          <textarea className="form-input" rows={3} value={value || ''} placeholder={fu.placeholder} onChange={set(fu.id)} />
        </div>
      );
    }
    return (
      <div className="field-group" key={fu.id}>
        <label className="field-label">{fu.label}</label>
        <input type="text" className="form-input" value={value || ''} placeholder={fu.placeholder} onChange={set(fu.id)} />
      </div>
    );
  };

  // A Yes/No question with optional follow-ups shown when answered "Yes".
  const renderYesNo = (item) => (
    <div className="field-group" key={item.id}>
      <label className="field-label">{item.label}</label>
      <div className="radio-group">
        {['Yes', 'No'].map((opt) => (
          <label className="radio-option" key={opt}>
            <input type="radio" name={item.id} value={opt} checked={formData[item.id] === opt} onChange={set(item.id)} />
            <span>{opt}</span>
          </label>
        ))}
      </div>
      {formData[item.id] === 'Yes' && item.followups && (
        <div className="followups">{item.followups.map(renderFollowup)}</div>
      )}
    </div>
  );

  return (
    <div className="universal-red-flags">
      <style>{`
        .universal-red-flags { border: 3px solid #dc2626; border-radius: 8px; padding: 20px; background: #fef2f2; }
        .universal-red-flags .rf-header { color: #991b1b; font-weight: 700; font-size: 18px; margin: 0 0 8px; }
        .universal-red-flags .rf-intro { background: #fff; border-left: 4px solid #f59e0b; padding: 12px 16px; border-radius: 6px; color: #78350f; margin-bottom: 16px; font-size: 14px; }
        .universal-red-flags .field-group { background: #fff; padding: 14px; border-radius: 6px; margin-bottom: 12px; border: 1px solid #e5e7eb; }
        .universal-red-flags .followups { margin-top: 12px; padding-left: 12px; border-left: 3px solid #fca5a5; }
        .universal-red-flags .rf-subheader { color: #991b1b; font-weight: 600; margin: 20px 0 8px; }
        .universal-red-flags .checkbox-group, .universal-red-flags .radio-group { display: flex; flex-wrap: wrap; gap: 12px; }
      `}</style>

      <h3 className="rf-header">Important health questions</h3>
      <div className="rf-intro">{UNIVERSAL_INTRO}</div>

      {UNIVERSAL_SCREEN.map(renderYesNo)}

      {injuryType === 'LUMBAR_SPINE' && (
        <>
          <div className="rf-subheader">A few more important questions</div>
          {LUMBAR_CE_RESCREEN.map(renderYesNo)}
        </>
      )}

      {injuryType === 'KNEE' && (
        <>
          <div className="rf-subheader">About your knee</div>
          {KNEE_OTTAWA.map(renderYesNo)}
          {renderYesNo(KNEE_LOCKED)}
        </>
      )}

      {injuryType === 'SHOULDER' && (
        <>
          <div className="rf-subheader">About your shoulder</div>
          <div className="field-group">
            <label className="field-label">{SHOULDER_ELEVATION.label}</label>
            <div className="radio-group">
              {SHOULDER_ELEVATION.options.map((opt) => (
                <label className="radio-option" key={opt.value}>
                  <input type="radio" name={SHOULDER_ELEVATION.id} value={opt.value} checked={formData[SHOULDER_ELEVATION.id] === opt.value} onChange={set(SHOULDER_ELEVATION.id)} />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      {supplementary && supplementary.items.length > 0 && (
        <>
          <div className="rf-subheader">A few more questions about this injury</div>
          {supplementary.items.map((item) => (
            <div className="field-group" key={item.id}>
              <label className="field-label">{item.label}</label>
              {renderOptions(item)}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default UniversalRedFlagsSection;
